import type { LucideIcon } from 'lucide-react'
import {
  Blocks,
  Boxes,
  CalendarClock,
  FileText,
  Layers,
  Package,
  Palette,
  Tags,
  UserCheck,
  Warehouse,
  ClipboardList,
  Contact,
  FileOutput,
  Inbox,
  ListChecks,
  PackageCheck,
  Receipt,
  FileSpreadsheet,
  Landmark,
  Send,
  Link2,
  Shield,
  UserPlus,
  Users,
  PieChart,
  Phone,
} from 'lucide-react'
import type { ModuleId } from '@/constants/modules'
import type { PermissionKey } from '@/types/permission.types'
import { ROUTES } from '@/constants/routes'

export interface MobileModuleNavTab {
  id: string
  label: string
  shortLabel?: string
  href: string
  icon: LucideIcon
  permission?: PermissionKey
  /** Active when true; default = path prefix match on href (ignore query). */
  isActive?: (pathname: string, search: string) => boolean
  /**
   * Soft transform-only reminder nudge when inactive (e.g. Follow up).
   * Uses `.animate-pmms-attention-nudge` — no JS timers; respects reduced motion.
   */
  attention?: boolean
}

export interface MobileModuleNavCreate {
  href: string
  label: string
  /** Compact bottom-nav caption (e.g. "Add"); defaults to label. */
  shortLabel?: string
  permission: PermissionKey
}

export interface MobileModuleNavConfig {
  id: string
  moduleId: ModuleId
  /** Path prefixes that activate this module chrome (`/sales/leads` covers nested routes). */
  matchPrefixes: string[]
  tabs: MobileModuleNavTab[]
  create?: MobileModuleNavCreate
}

function pathMatches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

/** Received / Assigned / My To Do — mirrors the list page's `?scope=` URL.
 * Scope is always written by TasksPage (including the default) so missing
 * `?scope=` only happens before the first write; treat it as Received. */
function taskScopeActive(scope: 'received' | 'assigned' | 'mine') {
  return (pathname: string, search: string) => {
    if (!pathMatches(pathname, ROUTES.TASKS)) return false
    if (pathname !== ROUTES.TASKS) return false
    const sp = new URLSearchParams(search).get('scope')
    if (sp) return sp === scope
    return scope === 'received'
  }
}

function salesTabActive(kind: 'lead' | 'follow' | 'registered' | 'summary' | 'old' | 'inbox') {
  return (pathname: string, search: string) => {
    if (!pathMatches(pathname, ROUTES.SALES_LEADS)) return false
    if (pathname.startsWith(`${ROUTES.SALES_LEADS}/old`)) return kind === 'old'
    if (pathname.startsWith(`${ROUTES.SALES_LEADS}/inbox`)) return kind === 'inbox'
    // Detail / create / register stay under Queries (not Registered / Follow up).
    if (pathname !== ROUTES.SALES_LEADS) return kind === 'lead'
    const sp = new URLSearchParams(search)
    const tab = sp.get('tab')
    // Bare /sales/leads opens Summary (users without it are re-synced to ?tab=lead at once).
    const bare = [...sp.keys()].length === 0
    if (kind === 'registered') return tab === 'registered'
    if (kind === 'follow') return tab === 'follow'
    if (kind === 'summary') return tab === 'summary' || bare
    if (kind === 'old') return tab === 'old'
    if (kind === 'inbox') return tab === 'inbox'
    return !bare && tab !== 'registered' && tab !== 'follow' && tab !== 'summary' && tab !== 'old' && tab !== 'inbox'
  }
}

/**
 * Contextual mobile bottom-nav modules.
 * Outside these prefixes the global Dashboard / Reports / Workspace / Settings bar shows.
 */
export const MOBILE_MODULE_NAV: MobileModuleNavConfig[] = [
  {
    id: 'sales-leads',
    moduleId: 'sales',
    matchPrefixes: [ROUTES.SALES_LEADS],
    tabs: [
      {
        id: 'summary',
        label: 'Summary',
        href: `${ROUTES.SALES_LEADS}?tab=summary`,
        icon: PieChart,
        permission: 'sales_leads.summary',
        isActive: salesTabActive('summary'),
      },
      {
        id: 'queries',
        label: 'Queries',
        href: `${ROUTES.SALES_LEADS}?tab=lead`,
        icon: Contact,
        permission: 'sales_leads.view',
        isActive: salesTabActive('lead'),
      },
      {
        id: 'inbox',
        label: 'Inbox',
        href: `${ROUTES.SALES_LEADS}?tab=inbox`,
        icon: Inbox,
        permission: 'sales_leads.inbox',
        isActive: salesTabActive('inbox'),
      },
      {
        id: 'registered',
        label: 'Registered',
        href: `${ROUTES.SALES_LEADS}?tab=registered`,
        icon: ClipboardList,
        permission: 'sales_leads.view',
        isActive: salesTabActive('registered'),
      },
      {
        id: 'follow',
        label: 'Follow up',
        href: `${ROUTES.SALES_LEADS}?tab=follow`,
        icon: CalendarClock,
        permission: 'sales_leads.view',
        isActive: salesTabActive('follow'),
        attention: true,
      },
      {
        id: 'old',
        label: 'Old',
        href: `${ROUTES.SALES_LEADS}?tab=old`,
        icon: Phone,
        permission: 'old_customers.view',
        isActive: salesTabActive('old'),
      },
    ],
    create: {
      href: ROUTES.SALES_LEADS_CREATE,
      label: 'Add query',
      shortLabel: 'Add',
      permission: 'sales_leads.create',
    },
  },
  {
    // Before `quotations` so /sales/quotations/terms gets its own Add (template).
    id: 'quotation-terms',
    moduleId: 'sales',
    matchPrefixes: [ROUTES.QUOTATION_TERMS_TEMPLATES],
    tabs: [
      {
        id: 'quotations',
        label: 'Quotations',
        href: ROUTES.QUOTATIONS,
        icon: Receipt,
        permission: 'quotations.view',
        isActive: () => false,
      },
      {
        id: 'terms',
        label: 'Terms',
        href: ROUTES.QUOTATION_TERMS_TEMPLATES,
        icon: FileText,
        permission: 'quotation_terms.view',
      },
    ],
    create: {
      href: `${ROUTES.QUOTATION_TERMS_TEMPLATES}?new=1`,
      label: 'Add template',
      shortLabel: 'Add',
      permission: 'quotation_terms.create',
    },
  },
  {
    id: 'quotations',
    moduleId: 'sales',
    matchPrefixes: [ROUTES.QUOTATIONS],
    tabs: [
      {
        id: 'quotations',
        label: 'Quotations',
        href: ROUTES.QUOTATIONS,
        icon: Receipt,
        permission: 'quotations.view',
        // Terms lives under /sales/quotations/terms — keep it out of this tab.
        isActive: (pathname) =>
          pathMatches(pathname, ROUTES.QUOTATIONS) && !pathMatches(pathname, ROUTES.QUOTATION_TERMS_TEMPLATES),
      },
      {
        id: 'terms',
        label: 'Terms',
        href: ROUTES.QUOTATION_TERMS_TEMPLATES,
        icon: FileText,
        permission: 'quotation_terms.view',
      },
    ],
    create: {
      href: ROUTES.QUOTATIONS_CREATE,
      label: 'New quotation',
      permission: 'quotations.create',
    },
  },
  {
    id: 'estimates',
    moduleId: 'sales',
    matchPrefixes: [ROUTES.ESTIMATES],
    tabs: [
      {
        id: 'estimates',
        label: 'Estimates',
        href: ROUTES.ESTIMATES,
        icon: FileSpreadsheet,
        permission: 'estimates.view',
      },
    ],
    create: {
      href: ROUTES.ESTIMATES_CREATE,
      label: 'New estimate',
      permission: 'estimates.create',
    },
  },
  {
    id: 'bank-accounts',
    moduleId: 'finance',
    matchPrefixes: [ROUTES.COMPANY_BANK_ACCOUNTS],
    tabs: [
      {
        id: 'bank-accounts',
        label: 'Bank accounts',
        href: ROUTES.COMPANY_BANK_ACCOUNTS,
        icon: Landmark,
        permission: 'bank_accounts.view',
      },
    ],
    create: {
      href: `${ROUTES.COMPANY_BANK_ACCOUNTS}?new=1`,
      label: 'Add bank',
      permission: 'bank_accounts.create',
    },
  },
  {
    id: 'dispatch',
    moduleId: 'dispatch',
    matchPrefixes: [ROUTES.DISPATCH_SHEET],
    tabs: [
      {
        id: 'dispatches',
        label: 'Dispatches',
        shortLabel: 'Dispatch',
        href: ROUTES.DISPATCH_SHEET,
        icon: FileOutput,
        permission: 'delivery_challans.view',
      },
    ],
    create: {
      href: ROUTES.DISPATCH_SHEET_CREATE,
      label: 'New dispatch',
      permission: 'delivery_challans.create',
    },
  },
  {
    id: 'purchases',
    moduleId: 'purchases',
    matchPrefixes: [ROUTES.PURCHASE_SHEET],
    tabs: [
      {
        id: 'purchases',
        label: 'Purchases',
        href: ROUTES.PURCHASE_SHEET,
        icon: PackageCheck,
        permission: 'goods_received.view',
      },
    ],
    create: {
      href: ROUTES.PURCHASE_SHEET_CREATE,
      label: 'New purchase',
      permission: 'goods_received.create',
    },
  },
  {
    id: 'tasks',
    moduleId: 'tasks',
    matchPrefixes: [ROUTES.TASKS],
    tabs: [
      {
        id: 'received',
        label: 'Received',
        href: `${ROUTES.TASKS}?scope=received`,
        icon: Inbox,
        permission: 'tasks.view',
        isActive: taskScopeActive('received'),
      },
      {
        id: 'assigned',
        label: 'Assigned',
        href: `${ROUTES.TASKS}?scope=assigned`,
        icon: Send,
        // Desktop shows this for tasks.assign OR tasks.view_all — MobileBottomNav
        // applies the OR gate (permission here is the base tasks.view).
        permission: 'tasks.view',
        isActive: taskScopeActive('assigned'),
      },
      {
        id: 'mine',
        label: 'My To Do',
        shortLabel: 'To Do',
        href: `${ROUTES.TASKS}?scope=mine`,
        icon: ListChecks,
        permission: 'tasks.view',
        isActive: taskScopeActive('mine'),
      },
    ],
    create: {
      href: ROUTES.TASKS_CREATE,
      label: 'Create task',
      permission: 'tasks.create',
    },
  },
  {
    id: 'users',
    moduleId: 'user-management',
    matchPrefixes: [ROUTES.USERS],
    tabs: [
      {
        id: 'users',
        label: 'Users',
        href: ROUTES.USERS,
        icon: Users,
        permission: 'users.view',
      },
      {
        id: 'agent-links',
        label: 'Agent links',
        shortLabel: 'Links',
        href: ROUTES.USERS_AGENT_LINKS,
        icon: Link2,
        permission: 'sales_agents.link',
      },
      {
        id: 'permissions',
        label: 'Permissions',
        shortLabel: 'Perms',
        href: ROUTES.PERMISSIONS,
        icon: Shield,
        permission: 'permissions.view',
      },
    ],
    create: {
      href: ROUTES.USERS_CREATE,
      label: 'Create user',
      permission: 'users.create',
    },
  },
  {
    id: 'customers',
    moduleId: 'master-data',
    matchPrefixes: [ROUTES.CUSTOMERS],
    tabs: [
      {
        id: 'parties',
        label: 'Parties',
        href: ROUTES.CUSTOMERS,
        icon: Users,
        permission: 'customers.view',
      },
    ],
    create: {
      href: ROUTES.CUSTOMERS_CREATE,
      label: 'Add party',
      permission: 'customers.create',
    },
  },
  {
    id: 'suppliers',
    moduleId: 'master-data',
    matchPrefixes: [ROUTES.SUPPLIERS],
    tabs: [
      {
        id: 'vendors',
        label: 'Vendors',
        href: ROUTES.SUPPLIERS,
        icon: UserPlus,
        permission: 'suppliers.view',
      },
    ],
    create: {
      href: ROUTES.SUPPLIERS_CREATE,
      label: 'Add vendor',
      permission: 'suppliers.create',
    },
  },
  {
    id: 'catalog',
    moduleId: 'master-data',
    matchPrefixes: [ROUTES.CATALOG],
    tabs: [
      {
        id: 'catalog',
        label: 'Catalog',
        href: ROUTES.CATALOG,
        icon: Boxes,
        permission: 'catalog.view',
      },
    ],
    create: {
      href: ROUTES.CATALOG_CREATE,
      label: 'Add catalog item',
      shortLabel: 'Add',
      permission: 'catalog.create',
    },
  },
  {
    id: 'designs',
    moduleId: 'master-data',
    matchPrefixes: [ROUTES.PRODUCTS],
    tabs: [
      {
        id: 'designs',
        label: 'Designs',
        href: ROUTES.PRODUCTS,
        icon: Package,
        permission: 'products.view',
      },
    ],
    create: {
      href: `${ROUTES.PRODUCTS}?new=1`,
      label: 'Add design',
      shortLabel: 'Add',
      permission: 'products.create',
    },
  },
  {
    id: 'categories',
    moduleId: 'master-data',
    matchPrefixes: [ROUTES.CATEGORIES],
    tabs: [
      {
        id: 'categories',
        label: 'Categories',
        href: ROUTES.CATEGORIES,
        icon: Tags,
        permission: 'categories.view',
      },
    ],
    create: {
      href: `${ROUTES.CATEGORIES}?new=1`,
      label: 'Add category',
      shortLabel: 'Add',
      permission: 'categories.create',
    },
  },
  {
    id: 'qualities',
    moduleId: 'master-data',
    matchPrefixes: [ROUTES.QUALITIES],
    tabs: [
      {
        id: 'qualities',
        label: 'Qualities',
        href: ROUTES.QUALITIES,
        icon: Layers,
        permission: 'qualities.view',
      },
    ],
    create: {
      href: `${ROUTES.QUALITIES}?new=1`,
      label: 'Add quality',
      shortLabel: 'Add',
      permission: 'qualities.create',
    },
  },
  {
    id: 'colours',
    moduleId: 'master-data',
    matchPrefixes: [ROUTES.COLOURS],
    tabs: [
      {
        id: 'colours',
        label: 'Colors',
        href: ROUTES.COLOURS,
        icon: Palette,
        permission: 'colors.view',
      },
    ],
    create: {
      href: `${ROUTES.COLOURS}?new=1`,
      label: 'Add color',
      shortLabel: 'Add',
      permission: 'colors.create',
    },
  },
  {
    id: 'sales-agents',
    moduleId: 'master-data',
    matchPrefixes: [ROUTES.SALES_AGENTS],
    tabs: [
      {
        id: 'sales-agents',
        label: 'Agents',
        href: ROUTES.SALES_AGENTS,
        icon: UserCheck,
        permission: 'sales_agents.view',
      },
    ],
    create: {
      href: `${ROUTES.SALES_AGENTS}?new=1`,
      label: 'Add agent',
      shortLabel: 'Add',
      permission: 'sales_agents.create',
    },
  },
  {
    id: 'warehouses',
    moduleId: 'master-data',
    matchPrefixes: [ROUTES.WAREHOUSES],
    tabs: [
      {
        id: 'warehouses',
        label: 'Warehouses',
        href: ROUTES.WAREHOUSES,
        icon: Warehouse,
        permission: 'warehouses.view',
      },
    ],
    create: {
      href: `${ROUTES.WAREHOUSES}?new=1`,
      label: 'Add warehouse',
      shortLabel: 'Add',
      permission: 'warehouses.create',
    },
  },
]

export function resolveMobileModuleNav(
  pathname: string,
): MobileModuleNavConfig | null {
  for (const config of MOBILE_MODULE_NAV) {
    if (config.matchPrefixes.some((prefix) => pathMatches(pathname, prefix))) {
      return config
    }
  }
  return null
}

export function isMobileModuleNavTabActive(
  tab: MobileModuleNavTab,
  pathname: string,
  search: string,
): boolean {
  if (tab.isActive) return tab.isActive(pathname, search)
  const hrefPath = tab.href.split('?')[0] ?? tab.href
  return pathMatches(pathname, hrefPath)
}

/** Exit control — returns to the mobile module launcher. */
export const MOBILE_MODULE_EXIT = {
  id: 'modules',
  label: 'Modules',
  href: ROUTES.WORKSPACE,
  icon: Blocks,
} as const
